// RD-PERF-003 — start the "is TOTP enrolled?" read at dashboard mount (parallel
// with the main load) instead of when MfaEncourageBanner finally mounts.
// In-flight dedupe per user; writes the same localStorage hint the banner and
// Account pill already read (`mfaStatusCache`). Never throws — a transient
// error resolves to the cached hint and the banner never flashes on it.
import { supabase } from '../../supabase';
import { getCachedMfa, setCachedMfa } from './mfaStatusCache';
import { totpFactorsFromList } from './totpFactors';

const inflight = new Map();
const settled = new Set(); // userIds whose prefetch finished this session
const listeners = new Set();
// RD-PERF-004: the dashboard satellite and the banner both ask at sign-in, ~140ms
// apart. A caller within this window reuses the settled value instead of re-reading.
// UX hint only (banner/pill) — never the 2FA gate.
export const MFA_PREFETCH_REUSE_MS = 10000;
const recent = new Map(); // userId -> { at, result }
let epoch = 0; // bumped by clearMfaStatusPrefetch: a pre-sign-out request writes nothing

function notify() {
  [...listeners].forEach((fn) => { try { fn(); } catch (_) { /* never break a reader */ } });
}

/** @returns {Promise<boolean|null>} verified-TOTP flag, or the cached hint on error. */
export function prefetchMfaStatus(userId) {
  if (!userId) return Promise.resolve(null);
  if (inflight.has(userId)) return inflight.get(userId);
  const hit = recent.get(userId);
  // Reuse only while the local hint still agrees (TwoFactorSettingsRow rewrites it
  // when the user turns 2FA on/off, which makes this a miss).
  if (hit && Date.now() - hit.at <= MFA_PREFETCH_REUSE_MS && getCachedMfa(userId) === hit.result) {
    return Promise.resolve(hit.result);
  }
  const startEpoch = epoch;
  const p = (async () => {
    let result = getCachedMfa(userId);
    let ok = false; // only a SUCCESSFUL read is reusable
    try {
      const { data } = await supabase.auth.mfa.listFactors();
      const has = Boolean(totpFactorsFromList(data).verified);
      if (startEpoch === epoch) setCachedMfa(userId, has);
      result = has;
      ok = true;
    } catch (_) { /* keep the hint */ }
    if (startEpoch !== epoch) return result; // signed out meanwhile — write nothing
    inflight.delete(userId);
    settled.add(userId);
    if (ok) recent.set(userId, { at: Date.now(), result });
    notify();
    return result;
  })();
  inflight.set(userId, p);
  return p;
}

/** True once a prefetch for this user finished (success or error). */
export function isMfaStatusSettled(userId) {
  return settled.has(userId);
}

export function subscribeMfaStatusPrefetch(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function clearMfaStatusPrefetch() {
  epoch += 1;
  inflight.clear();
  settled.clear();
  recent.clear(); // sign-out / user change: another account never reads this one's value
  notify();
}
